/**
 * console-detect.js — game-console UA classification + capability extension.
 *
 * Self-contained — probes native APIs only (navigator.userAgent / deviceMemory,
 * window.screen / devicePixelRatio, canvas WebGL UNMASKED_RENDERER, 'hid' in
 * navigator). No dependency on device-capabilities.js or any other module.
 * Returns a frozen snapshot via window.ConsoleDetect.get():
 *
 *   {
 *     platform: 'steamdeck' | 'ps5' | 'xbox' | 'switch' | 'generic',
 *     model:    string,            // e.g. 'PlayStation 5', 'Xbox Series X|S', 'Steam Deck', 'Nintendo Switch'
 *     gpu:      string,            // WebGL UNMASKED_RENDERER_WEBGL or ''
 *     ramHint:  number,            // navigator.deviceMemory — GB, 0 if unknown
 *     screenHint: { w, h, dpr },   // CSS pixels + DPR (for Steam Deck 1280x800 native detect)
 *     hasWebHID: boolean,          // 'hid' in navigator
 *     ua:       string             // raw UA (truncated 240 chars)
 *   }
 *
 * Console UA reference (curated from public sources):
 *   Steam Deck:  Mozilla/5.0 (X11; Linux x86_64) ... Chrome/... Safari/...  + steam? deck? markers
 *                Stock SteamOS Browser: contains 'SteamDeck' or 'Steam Browser'
 *   PS5:         Mozilla/5.0 (PlayStation; PlayStation 5/<v>) AppleWebKit/...
 *                Some firmware: also contains 'PlayStation 5' literal
 *   Xbox:        Mozilla/5.0 (Windows NT ...; Xbox; Xbox One) ... Edge/...
 *                Series X/S: 'Xbox One' is intentional Microsoft kept the marker
 *   Switch:      Mozilla/5.0 (Nintendo Switch; ...) AppleWebKit/...
 *                Captive browser: contains 'Nintendo Switch'
 *
 * No heavy fingerprinting — this is for tool-shape decisions (which UI to
 * pre-render, which pre-flight check to run), NOT for tracking.
 */
(function(){
    'use strict';

    if (window.ConsoleDetect && !window.ConsoleDetect.__isStub) return;

    var _snap = null;

    function _gpu() {
        try {
            var c = document.createElement('canvas');
            var gl = c.getContext('webgl2') || c.getContext('webgl');
            if (!gl) return '';
            var ext = gl.getExtension('WEBGL_debug_renderer_info');
            if (!ext) return '';
            return String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || '').slice(0, 200);
        } catch (_) { return ''; }
    }

    function _detect() {
        var ua = (navigator.userAgent || '').slice(0, 240);
        var platform = 'generic';
        var model = '';

        // Order matters — Switch UA contains 'Nintendo' which other matchers may
        // accidentally match on; check most specific first.
        if (/PlayStation\s*5/i.test(ua)) {
            platform = 'ps5';
            model = 'PlayStation 5';
        } else if (/PlayStation\s*4/i.test(ua)) {
            platform = 'ps5';   // tooling-wise we treat PS4 as same family — UI shows actual UA
            model = 'PlayStation 4';
        } else if (/Nintendo\s*Switch/i.test(ua)) {
            platform = 'switch';
            model = 'Nintendo Switch';
        } else if (/Xbox/i.test(ua)) {
            platform = 'xbox';
            // Xbox Series X|S still ship UA with "Xbox One" — use Edge version as proxy
            model = /Xbox\s*One/i.test(ua) ? 'Xbox (Series / One)' : 'Xbox';
        } else if (/SteamDeck|Steam\s*Deck|Valve\s*Steam/i.test(ua)) {
            platform = 'steamdeck';
            model = 'Steam Deck';
        } else if (/Linux\s+x86_64/i.test(ua) && /Chrome/i.test(ua)) {
            // SteamOS desktop-mode Chrome doesn't always brand itself.
            // Heuristic: Linux x86_64 Chrome at exactly 1280x800 = likely Deck.
            try {
                if (window.screen && window.screen.width === 1280 && window.screen.height === 800) {
                    platform = 'steamdeck';
                    model = 'Steam Deck (heuristic)';
                }
            } catch (_) {}
        }

        return Object.freeze({
            platform: platform,
            model:    model,
            gpu:      _gpu(),
            ramHint:  navigator.deviceMemory || 0,
            screenHint: Object.freeze({
                w:   (window.screen && window.screen.width) || 0,
                h:   (window.screen && window.screen.height) || 0,
                dpr: window.devicePixelRatio || 1
            }),
            hasWebHID: ('hid' in navigator),
            ua:       ua
        });
    }

    var ConsoleDetect = {
        get: function() {
            if (!_snap) _snap = _detect();
            return _snap;
        },
        // Lightweight booleans for blade conditionals
        is: function(p) { return this.get().platform === p; },
        // For tools that want UA-spoof override during testing (Playwright matrix)
        __forceForTest: function(snap) { _snap = Object.freeze(snap); }
    };

    window.ConsoleDetect = ConsoleDetect;

    // Mark as real impl (so __hasReal*() discriminators work, per
    // feedback_typeof_check_breaks_with_stub.md)
    Object.defineProperty(ConsoleDetect, '__isStub', { value: false });
})();
